import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Accordion,
  Box,
  Drawer,
  Flex,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import type { CartBrandDiscount } from '@iheartjane/types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { JaneGoldCallout } from '@jane/shared-ecomm/components';
import {
  useBrandSpecialsContext,
  useUserPreferences,
} from '@jane/shared-ecomm/providers';
import {
  trackBundlePromptTap,
  trackCheckoutClick,
} from '@jane/shared-ecomm/tracking';
import { getInitialDeliveryAddress } from '@jane/shared-ecomm/util';
import {
  useGetStore,
  useTargetedBrandSpecials,
} from '@jane/shared/data-access';
import { useShouldShowGold } from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct, StoreSpecial } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import {
  getBundlesInProgress,
  getEcommPaths,
  monolithPaths as paths,
} from '@jane/shared/util';

import { useCustomerDispatch } from '../../../../customer/dispatch';
import { stopShowBundlePossibilities } from '../../../../customer/redux/bundlePossibilities';
import {
  closeCart,
  removeDeliveryAddress,
  selectBagPickupMode,
  selectCart,
} from '../../../../customer/redux/cart';
import { selectCustomer } from '../../../../customer/redux/customer';
import { selectOperatorEmbedded } from '../../../../customer/redux/operatorEmbedded';
import { useCustomerSelector } from '../../../../customer/selectors';
import { useAuth } from '../../../../hooks/useAuth/useAuth';
import { useGuestCheckoutEnabled } from '../../../../hooks/useGuestCheckoutEnabled';
import { useSource } from '../../../../hooks/useSource';
import { applicableFreeDeliveryConfig } from '../../../../lib/userFreeDeliveryConfigs';
import { getCurrentStoreSpecials } from '../../../../sources/storeSpecials';
import { CartToppers } from '../../cartToppers/cartToppers';
import { CartDrawerAddress } from '../cartDrawerAddress/cartDrawerAddress';
import { CartValidationAlert } from '../cartDrawerAlerts/cartValidationAlert';
import { CartDrawerDiscountTerms } from '../cartDrawerDiscountTerms/cartDrawerDiscountTerms';
import { CartDrawerFooter } from '../cartDrawerFooter/cartDrawerFooter';
import { useCartReservationAlert } from '../cartDrawerHooks/useCartReservationAlert';
import { useGetCartLineItems } from '../cartDrawerHooks/useGetCartLineItems';
import { useReservationAvailability } from '../cartDrawerHooks/useReservationAvailability';
import { useValidateCart } from '../cartDrawerHooks/useValidateCart';
import { useValidateDeliveryAddress } from '../cartDrawerHooks/useValidateDeliveryAddress';
import { CartDrawerItem } from '../cartDrawerItems/cartDrawerItem';
import { CartDrawerSpecials } from '../cartDrawerSpecials/cartDrawerSpecials';
import { CartDrawerSummary } from '../cartDrawerSummary/cartDrawerSummary';
import { CartDrawerToggle } from '../cartDrawerToggle/cartDrawerToggle';
import { Divider, FinePrintWrapper } from './cartDrawer.styles';

export const CartDrawer = () => {
  const isMobile = useMobileMediaQuery({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useCustomerDispatch();
  const { search } = useLocation();
  const productsListRef = useRef<HTMLUListElement>(null);
  const prevProductCountRef = useRef<number | null>(null);

  const { appMode } = useEcommApp();

  const {
    defaultLogin,
    errors,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuth();

  const authentication = {
    errors,
    login: defaultLogin,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  };

  const { isChangingReservationModes, cart, deliveryAddress } =
    useCustomerSelector(selectCart);
  const currentReservationMode = cart.reservation_mode;
  const bagPickupMode = useCustomerSelector(selectBagPickupMode);
  const customer = useCustomerSelector(selectCustomer);
  const operatorEmbedded = useCustomerSelector(selectOperatorEmbedded);

  const {
    brand_discounts,
    brand_discounts_total,
    brand_discounts_service_fee_total,
    brand_discounts_total_with_fee,
    products,
    reservation_mode,
  } = cart;

  const { dismissBanner, dismissedBanners } = useBrandSpecialsContext();
  const qualifiedBanners = brand_discounts.filter(
    (discount) =>
      discount.special_type !== 'product' &&
      !dismissedBanners.includes(discount.brand_special_id)
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const [address, setAddress] = useState(
    getInitialDeliveryAddress(deliveryAddress)
  );
  const { setUserLocation, userPreferences } = useUserPreferences();
  const isGuestCheckoutEnabled = useGuestCheckoutEnabled();
  const {
    isFramelessEmbedMode,
    isBoostForAll,
    isPartnerHosted,
    partnerHostedPath,
  } = useRuntimeConfig();

  const { data: store } = useGetStore(cart?.store?.id);

  const ecommPaths = getEcommPaths(
    {
      appMode,
      isBoostForAll,
      isPartnerHosted,
      operatorId: operatorEmbedded?.operatorId,
      partnerHostedPath,
      storePathProps: {
        id: store?.id,
        name: store?.name,
        slug: store?.url_slug,
      },
    },
    search
  );

  const hideJanePayBranding = store
    ? Boolean(store?.jane_pay_integration?.white_label) ||
      Boolean(!store?.jane_pay_integration?.enabled)
    : false;

  const authenticated = customer?.authenticated;

  const { data: specialsData } = useSource<{ specials: StoreSpecial[] }>(
    getCurrentStoreSpecials(cart?.store?.id)
  );
  const { data } = useTargetedBrandSpecials(
    {
      storeIds: [cart.store?.id],
      appMode,
    },
    { enabled: !!cart.products.length }
  );

  const specials = specialsData ? specialsData.specials : [];

  const bundlesInProgress = getBundlesInProgress({
    brandSpecials: data?.brand_specials || [],
    cart,
  });
  const hasBundlesInProgress =
    bundlesInProgress && bundlesInProgress.length > 0;
  const shouldShowGold =
    useShouldShowGold({ store }) &&
    (!!brand_discounts_total || hasBundlesInProgress);

  const {
    deliveryValidationData,
    addressErrorMessage,
    resetAddressValidation,
    googleValidatedAddress,
  } = useValidateDeliveryAddress({
    store,
    address,
    street2: deliveryAddress?.street2,
  });

  // this is the only way of retrieving customer location data in boost
  // since the marketplace is essentially scoped to a single store and the user is not prompted
  // to share their location when browsing products.
  useEffect(() => {
    if (isFramelessEmbedMode && googleValidatedAddress) {
      // set in local storage
      setUserLocation({
        ...googleValidatedAddress,
        hasResetLocation: true,
      });
    }
  }, [googleValidatedAddress, isFramelessEmbedMode, setUserLocation]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef?.current?.scrollTo(0, 0);
    }
  }, [cart.reservation_mode]);

  const cartLineItems = useGetCartLineItems();

  // Curbside and pickup share the wire mode 'pickup'; bagPickupMode distinguishes
  // them (flag-gated so flag-off never resolves to curbside).
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;
  const selectedFulfillmentMode =
    (currentReservationMode || reservation_mode) === 'delivery'
      ? 'delivery'
      : curbsideFlagOn && bagPickupMode === 'curbside'
        ? 'curbside'
        : 'pickup';

  const {
    canGoToCheckout,
    checkoutButtonLabel,
    curbsideLabel,
    curbsideSchedule,
    deliveryLabel,
    deliverySchedule,
    hasCurbsideSlots,
    hasDeliverySlots,
    hasPickupSlots,
    isAcceptingDeliveryOrders,
    isLoading,
    modesToRender,
    pickupLabel,
    pickupSchedule,
    refetchCurbside,
    refetchDelivery,
    refetchPickup,
  } = useReservationAvailability({
    address: deliveryAddress,
    reservationMode: selectedFulfillmentMode,
    store,
  });

  const {
    cartReport,
    hasValidCart,
    minimumDeliveryOrderMet,
    minimumPickupOrderMet,
  } = useValidateCart({
    cartLineItems,
    deliveryValidationData,
    store,
  });

  const reservationAlert = useCartReservationAlert({
    curbsideLabel,
    curbsideSchedule,
    deliveryLabel,
    deliverySchedule,
    hasCurbsideSlots,
    hasDeliverySlots,
    hasPickupSlots,
    pickupLabel,
    pickupSchedule,
    reservationMode: selectedFulfillmentMode,
    store,
  });

  // .checkout() is wrapped in an authGate, so the user will be redirected to login if needed.
  const checkoutPath =
    !authenticated && isGuestCheckoutEnabled
      ? paths.guestCheckout()
      : paths.checkout();

  const handleSubmit = async () => {
    dispatch(stopShowBundlePossibilities());

    trackCheckoutClick(cart.reservation_mode);

    if (reservation_mode !== 'delivery') {
      dispatch(removeDeliveryAddress());
    }

    if (canGoToCheckout) {
      dispatch(closeCart());
      navigate(checkoutPath, {
        state: [{ title: 'previous', url: pathname }],
      });
    }
  };

  const handleSetAddress = (address: string) => {
    setAddress(address);
  };

  const cartSpecials = specials.find(
    (special) => special.id === cart.cart_discount_special_id
  )?.id;

  const groupSpecial = specials.find(
    (special) => special.id === cart.group_special_id
  )?.id;

  const appliedSpecials = cart?.products
    .map((product) => product.special_id)
    .filter((special, i, arr) => !!special && arr.indexOf(special) === i)
    .concat([cartSpecials, groupSpecial])
    .filter(Boolean);

  const freeDeliveryMinimum = applicableFreeDeliveryConfig({
    freeDeliveryConfigs: store?.free_delivery_configs,
    discountedSubtotal: cartLineItems?.discountedSubtotal,
  });

  useEffect(() => {
    const prevCount = prevProductCountRef.current;
    const currentCount = products?.length ?? 0;

    // Focus the product list when a product is added or removed while the
    // drawer is already open. Skip the initial render (prevCount is null)
    // so that opening the drawer doesn't scroll past delivery fields.
    if (prevCount !== null && currentCount > 0 && currentCount !== prevCount) {
      productsListRef.current?.focus({ preventScroll: true });
    }

    prevProductCountRef.current = currentCount;
  }, [products?.length]);

  /**
   * Refetch slots when cart drawer opens
   */
  useEffect(() => {
    if (currentReservationMode === 'pickup') {
      refetchPickup();
    }

    if (currentReservationMode === 'delivery' && address) {
      refetchDelivery();
    }
  }, []);

  return (
    <>
      <Drawer.Header
        divider
        title="Bag"
        subtitle={store?.name ? `${store.name}, ${store.city}` : ''}
      >
        {(!curbsideFlagOn || store) && (
          <CartDrawerToggle
            curbsideLabel={curbsideLabel}
            deliveryAddress={address}
            deliveryLabel={deliveryLabel}
            hasCurbsideSlots={hasCurbsideSlots}
            hasDeliverySlots={hasDeliverySlots}
            hasPickupSlots={hasPickupSlots}
            isAcceptingDeliveryOrders={isAcceptingDeliveryOrders}
            isLoading={isLoading}
            modesToRender={modesToRender}
            pickupLabel={pickupLabel}
            refetchCurbside={refetchCurbside}
            refetchDelivery={refetchDelivery}
            refetchPickup={refetchPickup}
          />
        )}
      </Drawer.Header>

      <Drawer.Content noPadding ref={contentRef}>
        <Box background="grays-white" height="100%" width="100%">
          <Flex flexDirection="column" height="100%">
            {reservation_mode === 'delivery' && (
              <CartDrawerAddress
                addressErrorMessage={addressErrorMessage}
                deliveryLabel={deliveryLabel}
                initialDeliveryAddress={address}
                handleSetAddress={handleSetAddress}
                resetAddressValidation={resetAddressValidation}
              />
            )}

            <CartValidationAlert
              brandDiscounts={qualifiedBanners}
              cart={cart}
              cartReport={cartReport}
              deliveryLabel={deliveryLabel}
              deliveryValidationData={deliveryValidationData}
              minimumDeliveryOrderMet={minimumDeliveryOrderMet}
              minimumPickupOrderMet={minimumPickupOrderMet}
              onDismissBrandDiscountBanner={dismissBanner}
              pickupLabel={pickupLabel}
              reservationAlert={reservationAlert}
              reservationMode={currentReservationMode}
              store={store}
            />

            <Box py={isMobile ? 8 : 24}>
              <ul
                ref={productsListRef}
                aria-label="Products in cart"
                tabIndex={-1}
              >
                {products.map((product: PendingCartProduct) => {
                  const productDetailLink = ecommPaths.menuProduct(product);

                  return (
                    <li key={product?.id + product.price_id}>
                      <CartDrawerItem
                        product={product}
                        productDetailLink={productDetailLink}
                        specials={specials}
                        storeId={cart?.store?.id}
                      />
                    </li>
                  );
                })}
              </ul>
            </Box>

            <Accordion>
              {shouldShowGold && (
                <>
                  <Divider />
                  <JaneGoldCallout
                    bundlesInProgress={bundlesInProgress}
                    cartBrandDiscounts={brand_discounts as CartBrandDiscount[]}
                    cartUuid={cart?.uuid}
                    discountLabel={brand_discounts_total}
                    feeLabel={brand_discounts_service_fee_total}
                    hideJanePayBranding={hideJanePayBranding}
                    onBrandSpecialClick={(brandSpecial) => {
                      if (brandSpecial.special_type !== 'product') {
                        trackBundlePromptTap({
                          brandSpecial,
                          cityState: `${store?.city}, ${store?.state}`,
                          source: 'bag',
                          storeFulfillmentType:
                            userPreferences?.storeFulfillmentType,
                          storeSearchRadius: userPreferences?.storeSearchRadius,
                          storeType: userPreferences?.storeType,
                          storeId: store?.id,
                          storeName: store?.name,
                          storeCity: store?.city,
                          storeState: store?.state,
                        });
                      }

                      navigate(
                        ecommPaths.menuBrandSpecial({
                          id: brandSpecial.id,
                          title: brandSpecial.title,
                        })
                      );

                      dispatch(closeCart());
                    }}
                    storeId={cart?.store?.id}
                    totalLabel={brand_discounts_total_with_fee}
                    variant="cart"
                    authentication={authentication}
                  />
                </>
              )}
              <Divider />
              <CartDrawerSpecials specials={specials} />
            </Accordion>

            <CartDrawerSummary
              cartLineItems={cartLineItems}
              isChangingReservationModes={isChangingReservationModes}
            />

            <CartToppers specials={specials} />

            {(appliedSpecials.length > 0 ||
              cartLineItems.discountTotal > 0 ||
              store?.state === 'California') && (
              <FinePrintWrapper flexDirection="column">
                {(appliedSpecials.length > 0 ||
                  cartLineItems.discountTotal > 0) && (
                  <CartDrawerDiscountTerms
                    appliedSpecials={appliedSpecials}
                    freeDeliveryMinimum={
                      reservation_mode === 'delivery'
                        ? freeDeliveryMinimum?.cart_minimum
                        : undefined
                    }
                    storeId={store?.id}
                    storeName={store?.name}
                    specialLabel={
                      store?.store_compliance_language_settings?.['specials']
                    }
                  />
                )}
              </FinePrintWrapper>
            )}
          </Flex>
        </Box>
      </Drawer.Content>
      <CartDrawerFooter
        checkoutDisabled={!canGoToCheckout || !hasValidCart}
        checkoutButtonLabel={checkoutButtonLabel}
        deliveryValidationData={deliveryValidationData}
        handleSubmit={handleSubmit}
        total={cartLineItems.total}
        specials={specials}
      />
    </>
  );
};
